
import React, { useState } from 'react';
import { AppointmentCard } from './AppointmentCard';
import { EmptyAppointments } from './EmptyAppointments';
import { Loader2 } from 'lucide-react';
import { Appointment } from '../types/appointmentTypes';
import { canRequestRefund } from '../utils/appointmentHelpers';
import { ChatModal } from '@/components/chat/ChatModal';
import { getFullName } from '@/utils/name-helpers';
import { Badge } from '@/components/ui/badge';

interface AppointmentListProps {
  appointments: Appointment[];
  isLoading: boolean;
  onRequestRefund: (appointmentId: string) => void;
  unreadByBooking?: Record<string, number>;
  onRefreshUnreadCounts?: () => void;
}

export const AppointmentList: React.FC<AppointmentListProps> = ({
  appointments,
  isLoading,
  onRequestRefund,
  unreadByBooking = {},
  onRefreshUnreadCounts,
}) => {
  const [chatBookingId, setChatBookingId] = useState<string | null>(null);
  const [chatPractitionerName, setChatPractitionerName] = useState<string>('');
  
  // Debug logging
  console.log('📋 AppointmentList - unreadByBooking:', unreadByBooking);

  const openChat = (bookingId: string, practitionerName: string) => {
    setChatBookingId(bookingId);
    setChatPractitionerName(practitionerName);
  };

  const closeChat = () => {
    setChatBookingId(null);
    setChatPractitionerName('');

    // Refresh unread counts in parent component after closing chat
    if (onRefreshUnreadCounts) {
      console.log('📱 AppointmentList: Refreshing unread counts after chat close');
      // Add a small delay to ensure the database has been updated
      setTimeout(() => {
        onRefreshUnreadCounts();
      }, 500);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[300px]">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  if (appointments.length === 0) {
    return <EmptyAppointments />;
  }

  return (
    <>
      <div className="grid gap-3 sm:gap-4 lg:gap-6 grid-cols-1 xl:grid-cols-2">
        {appointments.map((appointment) => {
          const practitionerName = getFullName(appointment.practitioner);
          
          return (
            <AppointmentCard 
              key={appointment.id} 
              appointment={appointment}
              onRequestRefund={onRequestRefund}
              isRefundable={canRequestRefund(appointment)}
              onOpenChat={() => openChat(appointment.id, practitionerName || 'Practitioner')}
              unreadCount={unreadByBooking[appointment.id] || 0}
            />
          );
        })}
      </div>

      {chatBookingId && (
        <ChatModal
          isOpen={!!chatBookingId}
          onClose={closeChat}
          bookingId={chatBookingId}
          otherParticipantName={chatPractitionerName}
          currentUserType="client"
        />
      )}
    </>
  );
};
